import React, { useState, useEffect } from 'react'
import { T, SectionLabel, Stat, Spinner, Empty, Avatar, SearchBar, searchMatch, pct, probColor } from '../ui'
import { getNFLScorecard, getNFLCalibration, getNFLTierRecord, getNFLFantasyAccuracy, getNFLResults, getNFLWeeklyRecord,
  getNFLGameRecord, getPushState, subscribePush, unsubscribePush, getNFLTop20Weekly, getNFLRedZoneRecord,
  getNFLRecordPlays } from '../api'
import { shareProof, shareResult, shareNflTopResults } from '../share'
import NotifySettings from './NotifySettings'
import { playText, teamNick } from '../plain'

// ── Notify me — opt in to a push (pre-game "plays are live" + weekly results) ──
// Exported so Home can surface the opt-in where users actually land, growing the
// push audience the pre-game featured push depends on.
// Renders only when the browser supports Web Push AND the backend has VAPID keys
// configured (dormant otherwise). On iOS this requires the app be added to the home
// screen — where it isn't, window.PushManager is absent so the card stays hidden.
export function NotifyCard({ settings } = {}) {
  const [st, setSt] = useState(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [showCfg, setShowCfg] = useState(false)
  useEffect(() => { let a = true; getPushState().then(s => { if (a) setSt(s) }); return () => { a = false } }, [])
  if (!st || !st.supported || !st.enabled) return null      // hidden until supported + configured
  const on = st.subscribed && st.permission === 'granted'
  const toggle = async () => {
    setBusy(true); setMsg('')
    if (on) { await unsubscribePush(); setSt(s => ({ ...s, subscribed: false })) }
    else {
      const r = await subscribePush()
      if (r.ok) { setSt(s => ({ ...s, subscribed: true, permission: 'granted' })); setMsg("You're set — we'll ping you when the week is graded.")
        try { const { syncFavsNow } = await import('../favorites'); syncFavsNow() } catch (_) {} }   // push your followed players to the alert watchlist
      else setMsg(r.reason === 'denied'
        ? 'Notifications are blocked — enable them for LINEBRKR in your browser settings.'
        : 'Could not enable notifications right now.')
    }
    setBusy(false)
  }
  return (
    <>
    <div style={{ background: T.card, border: `1px solid ${on ? T.greenMid : T.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 20, flexShrink: 0 }}>🔔</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Get the plays before kickoff</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 1, lineHeight: 1.4 }}>
            A ping when the week's featured plays go live on game-day morning — plus the graded results after. No spam.
          </div>
        </div>
        <button onClick={toggle} disabled={busy} style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 20, cursor: busy ? 'default' : 'pointer',
          fontSize: 12, fontWeight: 800, border: `1px solid ${on ? T.border : T.green}`,
          background: on ? T.card2 : T.green, color: on ? T.muted : '#04140D', opacity: busy ? 0.6 : 1 }}>
          {busy ? '…' : on ? 'On ✓' : 'Notify me'}
        </button>
      </div>
      {msg && <div style={{ fontSize: 10.5, color: on ? T.green : T.amber, marginTop: 8, lineHeight: 1.4 }}>{msg}</div>}
    </div>
    {on && settings && (
      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setShowCfg(v => !v)} style={{ width: '100%', textAlign: 'left', cursor: 'pointer',
          background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px',
          color: T.text, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>⚙️ Customize alerts</span>
          <span style={{ color: T.muted2, fontSize: 12 }}>{showCfg ? '▲' : '▼'}</span>
        </button>
        {showCfg && <NotifySettings />}
      </div>
    )}
    </>
  )
}

// ── This Week's Card — live hit/miss as games settle ─────────────────────────
const STATUS = {
  hit:      { label: '✓ Hit',      c: T.green,  bg: T.greenDim },
  live:     { label: '● LIVE',     c: T.blue,   bg: T.blueDim },
  miss:     { label: '✕ Miss',     c: T.red,    bg: T.redDim },
  push:     { label: 'Push',       c: T.amber,  bg: T.amberDim },
  settling: { label: '◷ Settling', c: T.blue,   bg: T.blueDim },
  pending:  { label: '⏳ Pending',  c: T.muted,  bg: T.card2 },
  dnp:      { label: 'DNP',        c: T.faint,  bg: T.card2 },
}

function StatusChip({ s }) {
  const st = STATUS[s] || STATUS.pending
  return <span style={{ fontSize: 10.5, fontWeight: 800, color: st.c, background: st.bg,
    border: `1px solid ${st.c}55`, borderRadius: 6, padding: '3px 8px', whiteSpace: 'nowrap' }}>{st.label}</span>
}

function WeekCard({ season, week, onPlayer }) {
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sample, setSample] = useState(false)
  const [scope, setScope] = useState('featured')   // featured board vs EVERY projected prop
  const [mkt, setMkt] = useState('ALL')
  const [q, setQ] = useState('')
  const [res, setRes] = useState('all')            // result filter: all / hit / miss / pending
  const [band, setBand] = useState('recommended')  // primary = recommended plays · secondary = monitored leans
  useEffect(() => {
    let a = true; setLoading(true)
    let timer = null
    const load = () => getNFLResults(season, week, scope).then(r => {
      if (!a) return
      setD(r); setSample(!!r._sample); setLoading(false)
      // auto-refresh every 30s while any game is live, so hits lock in real time.
      clearTimeout(timer)
      if (r && r.has_live) timer = setTimeout(load, 30000)
    })
    load()
    return () => { a = false; clearTimeout(timer) }
  }, [season, week, scope])
  if (loading) return <Spinner />
  // The advertised record is the RECOMMENDED plays only (Elite / Best Bet / Strong) — the plays
  // we actually stand behind — graded at the real posted line. Everything else is a monitored
  // lean, tracked for transparency but never counted in the headline. (Ranking a graded record
  // by the mutable live edge produced a nonsense perfect week; the record is the recommended set.)
  const allPlays = d?.plays || []
  const recSet = allPlays.filter(p => p.recommended)
    .sort((a, b) => ((b.edge_pct ?? -999) - (a.edge_pct ?? -999)) || ((b.prob || 0) - (a.prob || 0)))
    .map((p, i) => ({ ...p, _rank: i + 1 }))
  const restSet = allPlays.filter(p => !p.recommended)
    .sort((a, b) => ((b.prob || 0) - (a.prob || 0)))
  const tally = arr => {
    const w = arr.filter(p => p.status === 'hit').length
    const l = arr.filter(p => p.status === 'miss').length
    const pushes = arr.filter(p => p.status === 'push').length
    const pending = arr.filter(p => ['pending', 'live', 'settling'].includes(p.status)).length
    return { wins: w, losses: l, pushes, pending, hit_rate: (w + l) ? Math.round(100 * w / (w + l)) : null }
  }
  const current = band === 'rest' ? restSet : recSet
  const rec = tally(current)
  const VERIFIED_FROM_WEEK = 4                        // weeks before this predate durable book-line capture
  const verified = (week || 0) >= VERIFIED_FROM_WEEK
  const plays = current
  const graded = rec.wins + rec.losses
  // Of the graded recommended plays, how many were settled at a real posted book line vs our
  // projected line (no book number) — so the "verified" banner is honest per-play, not just by week.
  const bookGraded = current.filter(p => (p.status === 'hit' || p.status === 'miss') && p.line_source === 'book').length
  const projGraded = graded - bookGraded
  return (
    <div>
      {sample && <div style={{ fontSize: 10, color: T.amber, margin: '2px 0 8px', letterSpacing: '0.05em' }}>● Live results unavailable right now — check back shortly</div>}
      {d?.has_live && <div style={{ fontSize: 11, fontWeight: 800, color: T.blue, background: T.blueDim, border: `1px solid ${T.blue}55`, borderRadius: 8, padding: '6px 10px', marginBottom: 8, letterSpacing: '0.04em' }}>● LIVE — hits lock as they happen · auto-updating every 30s</div>}
      {/* Primary = Top 20 by conviction · Secondary = the rest. The record tiles below reflect the selected band. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {[['recommended', `⭐ Recommended`], ['rest', `Monitored${restSet.length ? ` · ${restSet.length}` : ''}`]].map(([id, lbl]) => {
          const on = band === id
          return <button key={id} onClick={() => { setBand(id); setRes('all') }} style={{ flex: 1, padding: '9px 0',
            borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 12.5, border: `1px solid ${on ? T.green : T.border}`,
            background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{lbl}</button>
        })}
      </div>
      {/* running record — for the selected band */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 10 }}>
        <Stat label="Record" value={graded ? `${rec.wins}-${rec.losses}${rec.pushes ? `-${rec.pushes}` : ''}` : '—'} />
        <Stat label="Hit rate" value={rec.hit_rate != null ? `${rec.hit_rate}%` : '—'} color={rec.hit_rate != null ? hitColor(rec.hit_rate) : T.muted} />
        <Stat label={d?.has_live ? 'Still live' : 'Pending'} value={rec.pending || 0} color={rec.pending ? T.blue : T.muted} />
      </div>
      <div style={{ fontSize: 10.5, color: T.muted, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 11px', marginBottom: 10, lineHeight: 1.5 }}>
        {band === 'recommended'
          ? <>The record above counts only our <b style={{ color: T.muted2 }}>recommended plays</b> (Elite / Best Bet / Strong) — the plays we stand behind — graded at the posted book line where one was available, else our projected line. Lower-conviction leans are tracked under <b style={{ color: T.muted2 }}>“Monitored.”</b></>
          : <>The <b style={{ color: T.muted2 }}>monitored leans</b> — lower-conviction plays graded for full transparency, never counted in the headline record.</>}
      </div>
      {!verified && (
        <div style={{ fontSize: 10.5, color: T.amber, background: T.amberDim || 'transparent', border: `1px solid ${T.amber}55`, borderRadius: 8, padding: '8px 11px', marginBottom: 10, lineHeight: 1.5 }}>
          ⚠ <b>Pre-audit week.</b> Graded before durable book-line capture went live, so unpriced plays leaned on the model line. Counted for continuity, but <b>Week {VERIFIED_FROM_WEEK}+ is the fully book-line-verified record.</b>
        </div>
      )}
      {verified && graded > 0 && (
        projGraded === 0
          ? <div style={{ fontSize: 10.5, color: T.green, background: T.greenDim, border: `1px solid ${(T.greenMid || T.green)}55`, borderRadius: 8, padding: '8px 11px', marginBottom: 10, lineHeight: 1.5 }}>
              ✓ <b>Book-verified</b> — all {graded} graded play{graded === 1 ? '' : 's'} settled at the real posted book line.
            </div>
          : <div style={{ fontSize: 10.5, color: T.green, background: T.greenDim, border: `1px solid ${(T.greenMid || T.green)}55`, borderRadius: 8, padding: '8px 11px', marginBottom: 10, lineHeight: 1.5 }}>
              ✓ <b>{bookGraded} of {graded}</b> graded at the real posted book line{projGraded ? <> · the other <b>{projGraded}</b> at our projected line (no book number posted), flagged for transparency</> : null}.
            </div>
      )}
      {/* Export a shareable proof card — the week's top N recommended plays with HIT/MISS. */}
      {band === 'recommended' && graded > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <span style={{ fontSize: 11, color: T.muted, fontWeight: 800, letterSpacing: '0.02em', marginRight: 2 }}>📤 Export</span>
          {[10, 20, 25].map(nn => (
            <button key={nn} onClick={() => shareNflTopResults(season, week, nn)} style={{ flex: 1, padding: '8px 0',
              borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 12, border: `1px solid ${T.border}`,
              background: T.card, color: T.green }}>Top {nn}</button>
          ))}
        </div>
      )}
      {/* Featured board vs every projected prop */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {[['featured', 'Featured'], ['all', 'All props']].map(([id, lbl]) => {
          const on = scope === id
          return <button key={id} onClick={() => { setScope(id); setMkt('ALL'); setQ('') }} style={{ flex: 1, padding: '8px 0',
            borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 12, border: `1px solid ${on ? T.green : T.border}`,
            background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{lbl}</button>
        })}
      </div>
      {scope === 'all' && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {['ALL', ...Array.from(new Set(plays.map(p => p.market)))].map(mk => {
              const on = mkt === mk
              return <button key={mk} onClick={() => setMkt(mk)} style={{ fontSize: 11, fontWeight: 800, padding: '5px 10px',
                borderRadius: 8, cursor: 'pointer', border: `1px solid ${on ? T.green : T.border}`,
                background: on ? T.greenDim : T.card, color: on ? T.green : T.muted }}>{mk === 'ALL' ? 'All markets' : mk}</button>
            })}
          </div>
          <SearchBar value={q} onChange={setQ} placeholder="Search player, team, or opponent…" />
        </>
      )}
      {/* Result filter — isolate hits, misses (to audit what went wrong), or what's still live. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {[['all', 'All'], ['hit', 'Hits'], ['miss', 'Misses'], ['pending', 'Pending']].map(([id, lbl]) => {
          const on = res === id
          const cnt = id === 'all' ? plays.length : plays.filter(p => p.status === id).length
          const col = id === 'hit' ? T.green : id === 'miss' ? T.red : id === 'pending' ? T.muted2 : T.green
          return <button key={id} onClick={() => setRes(id)} style={{ flex: 1, padding: '7px 0', borderRadius: 9, cursor: 'pointer',
            fontWeight: 800, fontSize: 11.5, border: `1px solid ${on ? col : T.border}`,
            background: on ? `${col}22` : T.card, color: on ? col : T.muted }}>{lbl} {cnt ? <span style={{ opacity: 0.7 }}>{cnt}</span> : ''}</button>
        })}
      </div>
      {(() => {
        const shown = plays.filter(p => (mkt === 'ALL' || p.market === mkt)
          && (res === 'all' || p.status === res)
          && searchMatch(q, r => [r.name, r.team, r.opp])(p))
        return !shown.length ? <Empty message={plays.length ? (res === 'miss' ? 'No misses — nothing to review here' : 'No plays match') : 'No plays surfaced for this week yet'} />
          : shown.map((p, i) => {
            const st = STATUS[p.status] || STATUS.pending
            const isTD = p.market === 'Anytime TD'
            const bet = isTD ? 'Anytime TD' : `${playText(p.market, p.line)}`
            const went = p.actual != null ? ` · went ${p.actual}` : p.live_actual != null ? ` · at ${p.live_actual}` : ''
            // Tapping a graded play opens the full modal (projection, The Case, matchup) so
            // a hit/miss can be reviewed, not just read. Needs a gsis to look the player up.
            const openable = onPlayer && p.gsis_id
            const open = () => openable && onPlayer({ gsis_id: p.gsis_id, full_name: p.name, name: p.name,
              pos: p.pos, team: p.team, opp: p.opp, market: p.market, line: p.line, prob_over: p.prob, status: 'active' })
            return (
              <div key={i} onClick={open} style={{ display: 'flex', alignItems: 'center', gap: 9, background: T.card,
                border: `1px solid ${st.c}44`, borderRadius: 12, padding: '10px 12px', marginBottom: 8,
                cursor: openable ? 'pointer' : 'default' }}>
                <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 13, color: T.blue, width: 28, flexShrink: 0 }}>#{p._rank}</span>
                <Avatar url={p.headshot} name={p.name} team={p.team} size={36} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.2 }}>{p.name}</div>
                  {/* Status + prop on their own line, matchup under it — a long name never squeezes them out. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                    {p.status === 'hit' ? (
                      <button onClick={(e) => { e.stopPropagation(); shareResult({ name: p.name, pos: p.pos, team: p.team, opp: p.opp,
                        market: p.market, line: p.line, actual: p.actual, prob: p.prob, week }) }}
                        title="Share this hit"
                        style={{ fontSize: 10, fontWeight: 800, color: st.c, background: st.bg, border: `1px solid ${st.c}`,
                          borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', textTransform: 'uppercase',
                          letterSpacing: '0.03em', cursor: 'pointer' }}>{st.label} ⤴</button>
                    ) : (
                      <span style={{ fontSize: 10, fontWeight: 800, color: st.c, background: st.bg, border: `1px solid ${st.c}`,
                        borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{st.label}</span>
                    )}
                    <span style={{ fontSize: 10, fontWeight: 700, color: T.muted2, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 6, padding: '2px 6px', whiteSpace: 'nowrap' }}>{bet}</span>
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{teamNick(p.team)}{p.opp ? ` · vs ${teamNick(p.opp)}` : ''}<span style={{ color: p.live_actual != null ? T.blue : T.muted2 }}>{went}</span></div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 48 }}>
                  <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 15, color: probColor(p.prob || 0) }}>{pct(p.prob || 0)}</div>
                  {/* Edge is the ranking metric now — show it so the order reads coherently; fall back to Rating. */}
                  {p.edge_pct != null
                    ? <div style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 700, color: p.edge_pct >= 0 ? T.green : T.red }}>{p.edge_pct >= 0 ? '+' : ''}{p.edge_pct.toFixed(1)}%</div>
                    : (p.conf ? <div style={{ fontFamily: T.mono, fontSize: 10, color: T.blue }}>R{p.conf}</div> : null)}
                </div>
              </div>
            )
          })
      })()}
    </div>
  )
}

const FPOS = ['QB', 'RB', 'WR', 'TE']
const maeColor = mae => mae == null ? T.muted : mae <= 5 ? T.green : mae <= 7 ? T.mint : mae <= 9 ? T.amber : T.red
const corrColor = r => r == null ? T.muted : r >= 0.7 ? T.green : r >= 0.55 ? T.mint : r >= 0.4 ? T.amber : T.red

const TIER_LABEL = { "ELITE":"Elite", "BEST BET":"Top Rated", "STRONG":"Strong",
  "PLAY":"Moderate", "WATCH":"Watch", "PASS":"Low" }

const PROP_LABEL = { receptions:'Receptions', anytime_td:'Anytime TD',
  rec_yards:'Rec Yds', rush_yards:'Rush Yds', pass_yards:'Pass Yds', pass_tds:'Pass TDs' }
const hitColor = h => h>=60?T.green : h>=52?T.mint : h>=48?T.amber : T.red

// "Top-rated" = the high-conviction bands — the signature "hit at high %" number.
const TOP_TIERS = new Set(['ELITE', 'BEST BET', 'STRONG'])
const topRated = tr => {
  const t = (tr?.tiers || []).filter(x => TOP_TIERS.has(x.tier))
  const plays = t.reduce((s, x) => s + x.plays, 0)
  const wins = t.reduce((s, x) => s + x.wins, 0)
  return plays >= 30 ? { plays, rate: Math.round((100 * wins) / plays) } : null
}

// Assemble the shareable proof card from the real track-record data. Leads with the
// top-rated band hit rate when it qualifies, else the overall hit rate; then adds only
// the supporting tiles that actually have data (record, recent form, book-line record,
// beat-the-close). Returns null until there's something real to show.
// Priced plays that must settle before the honest book-line record LEADS the headline.
// Below this we haven't graded enough real bets to headline it, so we fall back to the
// model-line conviction record (clearly labeled) rather than a thin book-line number.
const REAL_BOOK = 10

function buildProof({ sc, tr, wr }) {
  const o = sc?.overall || {}, rz = sc?.realized || {}, rf = wr?.recent_form
  const top = topRated(tr)
  const clvUp = rz.mean_clv != null && rz.mean_clv > 0
  // SHARPEST HEADLINE: closing-line value. Beating the close consistently is the single
  // most credible, variance-proof proof there is, so once we've graded enough priced plays
  // with a trustworthy CLV and the signal is clearly positive, it leads the share card too.
  const clvLeads = (rz.clv_n || 0) >= CLV_HEADLINE_MIN && (rz.clv_positive_rate || 0) >= 52
  const bookLeads = (rz.plays || 0) >= REAL_BOOK
  let big, bigLabel, context
  if (clvLeads) {
    big = `${rz.clv_positive_rate}%`; bigLabel = 'Beat the closing line'
    context = `${clvUp ? '+' : ''}${rz.mean_clv} pts avg line value · ${rz.clv_n.toLocaleString()} priced plays`
  } else if (bookLeads) {
    // HONEST HEADLINE: the record you'd have posted betting the ACTUAL book line
    // (out-of-sample, market-graded) — not our own reference line.
    big = `${rz.hit_rate}%`; bigLabel = 'Hit rate at the book line'
    context = `${rz.wins}-${rz.losses} · ${rz.plays.toLocaleString()} priced plays${rz.clv_n ? ` · beat the close ${rz.clv_positive_rate}%` : ''}`
  } else if (top) {
    big = `${top.rate}%`; bigLabel = 'Top-rated hit rate (model line)'; context = `Elite · Top Rated · Strong · ${top.plays.toLocaleString()} graded`
  } else if (o.plays) {
    big = `${o.hit_rate}%`; bigLabel = 'Model-line hit rate'; context = `${o.plays.toLocaleString()} plays graded`
  } else return null
  const tiles = []
  if (clvLeads) {
    if (rz.mean_clv != null) tiles.push({ label: 'Avg line value', value: `${clvUp ? '+' : ''}${rz.mean_clv} pts`, good: clvUp })
    if (rz.plays) tiles.push({ label: 'At the book line', value: `${rz.hit_rate}%`, good: rz.hit_rate >= 52 })
    if (rf) tiles.push({ label: `Last ${rf.weeks} wks`, value: `${rf.wins}-${rf.losses}`, good: rf.hit_rate >= 52 })
  } else if (bookLeads) {
    if (rz.clv_n) tiles.push({ label: 'Beat the close', value: `${rz.clv_positive_rate}%`, good: rz.clv_positive_rate >= 50 })
    if (o.plays) tiles.push({ label: 'Model line', value: `${o.hit_rate}%` })
    if (rf) tiles.push({ label: `Last ${rf.weeks} wks`, value: `${rf.wins}-${rf.losses}`, good: rf.hit_rate >= 52 })
  } else {
    if (o.plays) tiles.push({ label: 'Record', value: `${o.wins}-${o.losses}` })
    if (rf) tiles.push({ label: `Last ${rf.weeks} wks`, value: `${rf.wins}-${rf.losses}`, good: rf.hit_rate >= 52 })
    if (rz.plays) tiles.push({ label: 'At the book line', value: `${rz.hit_rate}%`, good: rz.hit_rate >= 52 })
    if (rz.clv_n) tiles.push({ label: 'Beat the close', value: `${rz.clv_positive_rate}%`, good: rz.clv_positive_rate >= 50 })
  }
  return { eyebrow: sc?.season ? `${sc.season} track record` : 'Track record',
    big, bigLabel, context, tiles,
    shareText: `LINEBRKR — ${bigLabel.toLowerCase()} ${big}${rf ? `, ${rf.wins}-${rf.losses} the last ${rf.weeks} weeks` : ''} · research tool, not betting advice` }
}

export default function RecordTab({ season, week, onPlayer }){
  const [mode, setMode] = useState('week')
  return (
    <div style={{ padding: '12px 16px 0' }}>
      <NotifyCard settings />
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {[['week', 'This Week'], ['receipts', 'Every Pick'], ['season', 'Track Record']].map(([id, label]) => {
          const on = id === mode
          return (
            <button key={id} onClick={() => setMode(id)} style={{
              flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 13,
              border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
              color: on ? T.green : T.muted }}>{label}</button>
          )
        })}
      </div>
      {mode === 'week' ? <WeekCard season={season || 2026} week={week || 1} onPlayer={onPlayer} />
        : mode === 'receipts' ? <Receipts season={season || 2026} onPlayer={onPlayer} />
        : <SeasonRecord season={season} />}
    </div>
  )
}

// ── Every Pick ("receipts") — every recommended play behind the record, grouped by week,
// filterable by market, so a user can find exactly who we called and who hit, week by week.
// Backed by /nfl/record-plays, which reconciles with the advertised scorecard by design.
function Receipts({ season, onPlayer }){
  const [mkt, setMkt] = useState('')
  const [d, setD] = useState(null)
  useEffect(()=>{ let a=true; setD(null); getNFLRecordPlays(season, mkt, 0).then(r=>{ if(a) setD(r) }); return ()=>{a=false} }, [season, mkt])
  // The market chips come from the full-season payload so they don't disappear when a filter
  // empties a market; fetch an unfiltered copy once for the chip set + the "All" totals.
  const [all, setAll] = useState(null)
  useEffect(()=>{ let a=true; getNFLRecordPlays(season, '', 0).then(r=>{ if(a) setAll(r) }); return ()=>{a=false} }, [season])
  if(!d || !all) return <Spinner/>
  const chips = [['','All markets'], ...Object.keys(all.by_market||{})
    .sort((x,y)=>((all.by_market[y].wins+all.by_market[y].losses)-(all.by_market[x].wins+all.by_market[x].losses)))
    .map(k => [k, (d.plays?.find(p=>p.prop_type===k)?.market) || MKT_SHORT[k] || k])]
  const rec = d.record || {}
  const plays = d.plays || []
  // Group by week, newest first.
  const weeks = {}
  for(const p of plays){ (weeks[p.week] = weeks[p.week] || []).push(p) }
  const weekNums = Object.keys(weeks).map(Number).sort((a,b)=>b-a)
  const label = mkt ? (chips.find(c=>c[0]===mkt)?.[1] || mkt) : 'All markets'
  return (
    <div style={{ padding:'0 0 80px' }}>
      <SectionLabel>Every recommended pick · {season}</SectionLabel>
      <div style={{ fontSize:10.5, color:T.muted, background:T.card2, border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 11px', marginBottom:10, lineHeight:1.5 }}>
        The exact plays behind our record — <b style={{color:T.muted2}}>every one we stood behind</b>, who hit and who didn't, by week. Tap a name to open the full card. These reconcile with the Track Record totals.
      </div>
      {/* Market filter — the fast way to find "how did our anytime-TD calls do" */}
      <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:8, marginBottom:4, WebkitOverflowScrolling:'touch' }}>
        {chips.map(([id,lab])=>{
          const on = id === mkt
          return <button key={id||'all'} onClick={()=>setMkt(id)} style={{ flexShrink:0, padding:'6px 12px', borderRadius:999,
            cursor:'pointer', fontWeight:800, fontSize:12, whiteSpace:'nowrap',
            border:`1px solid ${on?T.green:T.border}`, background:on?T.greenDim:T.card, color:on?T.green:T.muted }}>{lab}</button>
        })}
      </div>
      {/* Headline for the current filter */}
      <div style={{ display:'flex', alignItems:'baseline', gap:12, background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:'12px 14px', marginBottom:14 }}>
        <span style={{ fontFamily:T.mono, fontSize:30, fontWeight:900, color:hitColor(rec.hit_rate||0) }}>{rec.wins||0}&ndash;{rec.losses||0}</span>
        <span style={{ fontFamily:T.mono, fontSize:18, fontWeight:800, color:hitColor(rec.hit_rate||0) }}>{rec.hit_rate!=null?`${rec.hit_rate}%`:'—'}</span>
        <span style={{ fontSize:11.5, color:T.muted, marginLeft:'auto', textAlign:'right' }}>{label}<br/>{rec.plays||0} graded picks</span>
      </div>
      {!plays.length ? <Empty message={all.record?.plays ? 'No graded picks in this market yet' : 'Picks appear here once the week settles'}/> :
        weekNums.map(wk => {
          const ps = weeks[wk]
          const w = ps.filter(p=>p.result==='HIT').length, l = ps.length - w
          return (
            <div key={wk} style={{ marginBottom:16 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:7 }}>
                <span style={{ fontSize:13, fontWeight:900, color:T.text }}>Week {wk}</span>
                <span style={{ fontFamily:T.mono, fontSize:13, fontWeight:800, color:hitColor(w+l?100*w/(w+l):0) }}>{w}&ndash;{l}</span>
              </div>
              {ps.map((p,i)=>{
                const hit = p.result === 'HIT'
                const openable = onPlayer && p.gsis_id
                const open = () => openable && onPlayer({ gsis_id:p.gsis_id, full_name:p.player, name:p.player,
                  team:p.team, market:p.market, line:p.line, prob_over:(p.shown_pct||0)/100, status:'active' })
                return (
                  <div key={i} onClick={open} style={{ display:'flex', alignItems:'center', gap:10, background:T.card,
                    border:`1px solid ${(hit?T.green:T.red)}44`, borderRadius:11, padding:'9px 12px', marginBottom:7,
                    cursor:openable?'pointer':'default' }}>
                    <span style={{ fontSize:15 }}>{hit?'✅':'❌'}</span>
                    <div style={{ minWidth:0, flex:1 }}>
                      <div style={{ fontSize:13.5, fontWeight:800, color:T.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.player} <span style={{ color:T.faint, fontWeight:600 }}>· {p.team}</span></div>
                      <div style={{ fontSize:10.5, color:T.muted }}>{playText(p.prop_type || p.market, p.line)} · shown {p.shown_pct}%</div>
                    </div>
                    <span style={{ fontFamily:T.mono, fontSize:11, fontWeight:800, color:hit?T.green:T.red }}>{hit?'HIT':'MISS'}</span>
                  </div>
                )
              })}
            </div>
          )
        })}
    </div>
  )
}

// Short market labels for filter chips before any row of that market is loaded.
const MKT_SHORT = { anytime_td:'Anytime TD', rec_yards:'Rec Yds', rush_yards:'Rush Yds',
  pass_yards:'Pass Yds', pass_tds:'Pass TDs', receptions:'Receptions' }

function WeeklyTrend({ data }){
  const weeks = (data?.weeks || []).filter(w => w.plays)
  if (weeks.length < 2) return null                 // needs at least a couple of settled weeks
  const rf = data.recent_form
  const max = Math.max(100, ...weeks.map(w => w.hit_rate || 0))
  return (
    <>
      <SectionLabel right={rf ? `Last ${rf.weeks}: ${rf.wins}-${rf.losses}` : ''}>Weekly trend</SectionLabel>
      <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:84, marginBottom:8 }}>
          {weeks.map(w => (
            <div key={w.week} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:0 }}>
              <span style={{ fontFamily:T.mono, fontSize:10, color:hitColor(w.hit_rate||0), fontWeight:700 }}>{w.hit_rate!=null?`${Math.round(w.hit_rate)}`:'—'}</span>
              <div title={`Week ${w.week}: ${w.wins}-${w.losses}`} style={{ width:'100%', maxWidth:26,
                height:`${Math.max(4, ((w.hit_rate||0)/max)*58)}px`,
                background:hitColor(w.hit_rate||0), borderRadius:4, opacity:0.9 }}/>
              <span style={{ fontSize:10, color:T.faint }}>W{w.week}</span>
            </div>
          ))}
        </div>
        <div style={{ height:1, background:T.border, marginBottom:6 }}/>
        <div style={{ fontSize:10, color:T.faint, lineHeight:1.5 }}>
          Over-line hit rate per settled week (the surfaced board's plays). The 50% line is a coin flip — bars above it are green.
        </div>
      </div>
    </>
  )
}

// Reliability diagram — the credibility centerpiece. Each dot is a probability band
// plotted at (what we predicted, what actually happened); the dashed diagonal is perfect
// calibration. Dots on the line = our percentages are honest. Dot size = sample size,
// color = distance from the line (green on it, red far off).
// Merge narrow calibration buckets into wide bands so a small sample doesn't draw a zigzag of
// 10-game dots (each band's predicted/observed are n-weighted averages of its buckets).
function widenBuckets(buckets){
  const bands = [[50, 60], [60, 70], [70, 101]]
  return bands.map(([lo, hi]) => {
    const bs = (buckets || []).filter(b => b.n && b.pred_pct != null && b.lo * 100 >= lo && b.lo * 100 < hi)
    const n = bs.reduce((a, b) => a + b.n, 0)
    if (!n) return null
    const w = k => Math.round(10 * bs.reduce((a, b) => a + b[k] * b.n, 0) / n) / 10
    return { lo: lo / 100, hi: Math.min(hi, 100) / 100, n, pred_pct: w('pred_pct'), obs_pct: w('obs_pct'),
             label: `${lo}-${Math.min(hi, 100)}%` }
  }).filter(Boolean)
}

function ReliabilityDiagram({ buckets }){
  const pts = (buckets || []).filter(b => b.pred_pct != null && b.obs_pct != null && b.n)
  if (pts.length < 2) return null
  const S = 260, pad = 34
  const x = v => pad + (v / 100) * (S - 2 * pad)
  const y = v => (S - pad) - (v / 100) * (S - 2 * pad)   // SVG y grows down → invert
  const maxN = Math.max(...pts.map(p => p.n))
  const rOf = n => 3 + 6 * Math.sqrt(n / maxN)
  const calColor = d => d <= 4 ? T.green : d <= 8 ? T.amber : T.red
  const grid = [0, 25, 50, 75, 100]
  const ordered = pts.slice().sort((a, b) => a.pred_pct - b.pred_pct)
  return (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:'12px 12px 8px', marginBottom:8 }}>
      <div style={{ display:'flex', justifyContent:'center' }}>
        <svg viewBox={`0 0 ${S} ${S}`} width="100%" style={{ maxWidth:320 }} role="img" aria-label="Calibration reliability diagram: predicted vs observed hit rate">
          {grid.map(g => (
            <g key={g}>
              <line x1={x(g)} y1={pad} x2={x(g)} y2={S-pad} stroke={T.border} strokeOpacity="0.5" />
              <line x1={pad} y1={y(g)} x2={S-pad} y2={y(g)} stroke={T.border} strokeOpacity="0.5" />
              <text x={x(g)} y={S-pad+14} fill={T.faint} fontSize="8" textAnchor="middle">{g}</text>
              <text x={pad-6} y={y(g)+3} fill={T.faint} fontSize="8" textAnchor="end">{g}</text>
            </g>
          ))}
          <line x1={x(0)} y1={y(0)} x2={x(100)} y2={y(100)} stroke={T.muted} strokeDasharray="4 3" strokeOpacity="0.7" />
          <polyline fill="none" stroke={T.green} strokeOpacity="0.35" strokeWidth="1.5"
            points={ordered.map(p => `${x(p.pred_pct)},${y(p.obs_pct)}`).join(' ')} />
          {pts.map((p, i) => { const d = Math.abs(p.pred_pct - p.obs_pct); return (
            <circle key={i} cx={x(p.pred_pct)} cy={y(p.obs_pct)} r={rOf(p.n)} fill={calColor(d)} fillOpacity="0.85" stroke={T.bg} strokeWidth="1">
              <title>{`${p.label}: predicted ${p.pred_pct}% · observed ${p.obs_pct}% · n=${p.n}`}</title>
            </circle>
          )})}
          <text x={S/2} y={S-3} fill={T.muted} fontSize="9" textAnchor="middle">Predicted %</text>
          <text x={11} y={S/2} fill={T.muted} fontSize="9" textAnchor="middle" transform={`rotate(-90 11 ${S/2})`}>Observed %</text>
        </svg>
      </div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, flexWrap:'wrap', marginTop:2, fontSize:10, color:T.muted }}>
        <span><span style={{ display:'inline-block', width:16, borderTop:`1.5px dashed ${T.muted}`, verticalAlign:'middle', marginRight:4 }}/>perfect</span>
        <span><span style={{ display:'inline-block', width:8, height:8, borderRadius:4, background:T.green, verticalAlign:'middle', marginRight:4 }}/>on the line</span>
        <span>dot size = sample</span>
      </div>
    </div>
  )
}

// Week-over-week detailed breakdown of the top-20 conviction plays. Each week is a
// collapsible card (record header + the full play list, ✓/✗). The most recent week opens
// by default; older weeks collapse so the view stays scannable.
function Top20Weekly({ season }){
  const [d, setD] = useState(null)
  const [open, setOpen] = useState({})
  const [n, setN] = useState(20)
  useEffect(()=>{ let a=true; setD(null); getNFLTop20Weekly(season||2026, n).then(r=>{ if(!a) return
    setD(r); if((r.weeks||[]).length) setOpen({ [r.weeks[0].week]: true }) }); return ()=>{a=false} },[season, n])
  const nSel = (
    <div style={{ display:'flex', gap:6, marginBottom:10 }}>
      <span style={{ fontSize:11, color:T.muted, alignSelf:'center', marginRight:2 }}>Show top</span>
      {[10,20,25].map(v=>{ const on=v===n; return (
        <button key={v} onClick={()=>setN(v)} style={{ cursor:'pointer', fontSize:11.5, fontWeight:800, padding:'5px 11px',
          borderRadius:8, border:`1px solid ${on?T.green:T.border}`, background:on?T.greenDim:T.card, color:on?T.green:T.muted }}>{v}</button>
      )})}
    </div>
  )
  if(!d) return <><Spinner/></>
  const weeks = d.weeks || []
  if(!weeks.length) return <>{nSel}<Empty message="Top-plays breakdown fills in after the first settled week"/></>
  const ov = d.overall || {}
  return (
    <div>
      <div style={{ fontSize:10.5, color:T.muted, margin:'2px 0 8px', lineHeight:1.5 }}>
        The <b style={{color:T.text}}>top {n} highest-conviction plays</b> each week and exactly how each one landed — graded at the shown line, recommended tiers only. <b style={{color:T.muted2}}>CLV</b> = points the market moved our way after posting.
      </div>
      {nSel}
      {ov.plays ? (
        <div style={{ display:'flex', alignItems:'baseline', gap:8, background:T.greenDim, border:`1px solid ${T.green}`,
          borderRadius:10, padding:'10px 14px', marginBottom:12 }}>
          <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:24, color:hitColor(ov.hit_rate||0), lineHeight:1 }}>{ov.hit_rate}%</span>
          <span style={{ fontSize:12, color:T.muted }}>{ov.wins}-{ov.losses} · up to {n} a week, all weeks</span>
        </div>
      ) : null}
      {weeks.map(w => {
        const isOpen = !!open[w.week]
        return (
          <div key={w.week} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, marginBottom:8, overflow:'hidden' }}>
            <button onClick={()=>setOpen(o=>({ ...o, [w.week]: !o[w.week] }))} style={{ width:'100%', display:'flex',
              alignItems:'center', justifyContent:'space-between', gap:10, background:'transparent', border:'none',
              padding:'11px 14px', cursor:'pointer', color:T.text, textAlign:'left' }}>
              <span style={{ fontSize:14, fontWeight:800 }}>Week {w.week}
                {w.plays < n && <span style={{ fontSize:11, fontWeight:600, color:T.muted, marginLeft:8 }}>only {w.plays} qualified</span>}</span>
              <span style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontFamily:T.mono, fontSize:12.5, color:T.muted }}>{w.wins}-{w.losses}</span>
                <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:14, color:hitColor(w.hit_rate||0), minWidth:44, textAlign:'right' }}>{w.hit_rate}%</span>
                <span style={{ color:T.muted2, fontSize:12 }}>{isOpen?'▲':'▼'}</span>
              </span>
            </button>
            {isOpen && (
              <div style={{ borderTop:`1px solid ${T.border}` }}>
                {(w.list||[]).map((p,i) => {
                  const hit = p.result === 'hit'
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 14px', borderTop:i?`1px solid ${T.border}`:'none' }}>
                      <span style={{ flexShrink:0, width:20, height:20, borderRadius:'50%', display:'flex', alignItems:'center',
                        justifyContent:'center', fontSize:12, fontWeight:900, color: hit?T.green:T.red,
                        background: hit?'rgba(0,217,126,0.14)':'rgba(239,90,82,0.14)', border:`1px solid ${hit?T.green:T.red}` }}>{hit?'✓':'✕'}</span>
                      <div style={{ minWidth:0, flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                        <div style={{ fontSize:10.5, color:T.muted }}>{p.team} · {playText(p.market, p.line)}{p.actual!=null?<span style={{color:T.faint}}> · had {p.actual}</span>:null}</div>
                      </div>
                      <span style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', flexShrink:0, minWidth:44 }}>
                        <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:13, color:probColor(p.prob||0) }}>{Math.round((p.prob||0)*100)}%</span>
                        {p.clv!=null ? <span style={{ fontFamily:T.mono, fontSize:10, fontWeight:700,
                          color: p.clv>0?T.green:(p.clv<0?T.red:T.muted) }}>CLV {p.clv>0?'+':''}{p.clv}</span> : null}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// The credibility centerpiece. When we've graded enough priced plays with a trustworthy
// closing line, CLV — how often the market moved to OUR number after we posted — LEADS the
// record, because beating the close is the single sharpest, most variance-proof evidence of
// a real edge (a hot hit rate can be luck; consistently beating the close cannot). Below that
// sample, or when the CLV signal isn't clearly positive, we fall back to the prediction record
// (model-line hit rate). Either way the book-line record + prediction record sit underneath as
// support, so the honest market-graded numbers are always one glance away.
const CLV_HEADLINE_MIN = 20   // priced plays with a trustworthy CLV needed to headline beat-the-close (matches Home)

function ProofTile({ label, value, sub, color }){
  return (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, padding:"9px 11px" }}>
      <div style={{ fontSize:10, color:T.muted, letterSpacing:"0.04em", textTransform:"uppercase" }}>{label}</div>
      <div style={{ display:"flex", alignItems:"baseline", gap:6, marginTop:3 }}>
        <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:18, color:color||T.text, lineHeight:1 }}>{value}</span>
        {sub ? <span style={{ fontFamily:T.mono, fontSize:11, color:T.muted }}>{sub}</span> : null}
      </div>
    </div>
  )
}

function ProofHero({ sc, proof }){
  const o = sc?.overall || {}
  const rz = sc?.realized || {}
  if (!o.plays && !rz.clv_n) return null
  const ShareBtn = proof ? (
    <button onClick={()=>shareProof(proof)} aria-label="Share proof" title="Share proof"
      style={{ background:"none", border:"none", cursor:"pointer", color:T.green, padding:2, lineHeight:0 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>
    </button>
  ) : null
  const clvLeads = (rz.clv_n || 0) >= CLV_HEADLINE_MIN && (rz.clv_positive_rate || 0) >= 52
  const clvUp = rz.mean_clv != null && rz.mean_clv > 0
  if (clvLeads) {
    return (
      <div style={{ background:T.greenDim, border:`1px solid ${T.green}`, borderRadius:12, padding:"14px 16px", marginBottom:12 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontSize:11, color:T.green, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase" }}>Closing-line value{sc.season?` · ${sc.season}`:''}</div>
          {ShareBtn}
        </div>
        <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:4, flexWrap:"wrap" }}>
          <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:38, color:hitColor(rz.clv_positive_rate), lineHeight:1 }}>{rz.clv_positive_rate}%</span>
          <span style={{ fontSize:13.5, fontWeight:800, color:T.text }}>beat the closing line</span>
        </div>
        <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:12.5, color:clvUp?T.green:(rz.mean_clv<0?T.red:T.muted),
            background:T.card2, border:`1px solid ${clvUp?T.green:T.border}`, borderRadius:999, padding:"2px 10px" }}>{clvUp?'+':''}{rz.mean_clv} pts avg line value</span>
          <span style={{ fontSize:11.5, color:T.muted }}>{rz.clv_n.toLocaleString()} priced plays</span>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginTop:11 }}>
          <ProofTile label="At the book line" value={rz.hit_rate!=null?`${rz.hit_rate}%`:'—'} sub={rz.plays?`${rz.wins}-${rz.losses}`:''} color={rz.hit_rate!=null?hitColor(rz.hit_rate):T.muted}/>
          <ProofTile label="Prediction record" value={o.hit_rate!=null?`${o.hit_rate}%`:'—'} sub={o.plays?`${o.wins}-${o.losses}`:''} color={o.hit_rate!=null?hitColor(o.hit_rate):T.muted}/>
        </div>
        <div style={{ fontSize:10.5, color:T.muted2, marginTop:9, lineHeight:1.5 }}>
          <b style={{color:T.green}}>Beating the close</b> is the sharpest proof there is — it means the market moved toward our number <i>after</i> we posted, so the edge was real, not a hot streak. Live results only, no backtests.
        </div>
      </div>
    )
  }
  // Fallback — the prediction record (model-line hit rate) leads until the CLV sample qualifies.
  if (!o.plays) return null
  return (
    <div style={{ background:T.greenDim, border:`1px solid ${T.green}`, borderRadius:12, padding:"14px 16px", marginBottom:12 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ fontSize:11, color:T.green, fontWeight:800, letterSpacing:"0.06em", textTransform:"uppercase" }}>Our prediction record</div>
        {ShareBtn}
      </div>
      <div style={{ display:"flex", alignItems:"baseline", gap:8, marginTop:4 }}>
        <span style={{ fontFamily:T.mono, fontWeight:800, fontSize:36, color:hitColor(o.hit_rate||0), lineHeight:1 }}>{o.hit_rate}%</span>
        <span style={{ fontSize:12.5, color:T.muted }}>{o.wins}-{o.losses} · {o.plays.toLocaleString()} plays</span>
      </div>
      <div style={{ fontSize:11.5, color:T.muted2, marginTop:6, lineHeight:1.45 }}>
        How often the side our model projected actually hit{sc.season?`, ${sc.season} season`:''} — live results only, no backtests.
        {sc.top20?.plays ? <> Season's 20 highest-conviction plays: <b style={{color:T.green}}>{sc.top20.hit_rate}%</b> ({sc.top20.wins}-{sc.top20.losses}).</> : null}
        {(rz.clv_n||0) > 0 && rz.clv_positive_rate!=null ? <> Beat the close <b style={{color:T.green}}>{rz.clv_positive_rate}%</b> so far ({rz.clv_n}).</> : null}
      </div>
    </div>
  )
}

function SeasonRecord({ season }){
  const [sub, setSub] = useState('record')
  const [sc, setSc] = useState(null)
  const [cal, setCal] = useState(null)
  const [tr, setTr] = useState(null)
  const [fa, setFa] = useState(null)
  const [wr, setWr] = useState(null)
  const [gr, setGr] = useState(null)
  const [pd, setPd] = useState(null)   // Pay Dirt (red-zone TD board) track record
  const [gmOpen, setGmOpen] = useState(false)   // game model (in testing) — collapsed by default
  useEffect(()=>{ let a=true
    getNFLScorecard(season||2025,0).then(r=>{ if(a) setSc(r) })
    getNFLCalibration(season||2025).then(r=>{ if(a) setCal(r) })
    getNFLTierRecord(season||2025,0).then(r=>{ if(a) setTr(r) })
    getNFLFantasyAccuracy('ppr').then(r=>{ if(a) setFa(r) })
    getNFLWeeklyRecord(season||2025).then(r=>{ if(a) setWr(r) })
    getNFLGameRecord(season||2025,0).then(r=>{ if(a) setGr(r) })
    getNFLRedZoneRecord(season||2026).then(r=>{ if(a) setPd(r) })
    return ()=>{a=false} },[season])
  if(!sc) return <Spinner/>
  const props = Object.entries(sc.by_prop||{})
  const proof = buildProof({ sc, tr, wr })
  return (
    <div style={{ padding:"0 0 80px" }}>
      <SectionLabel right={proof ? '⤴ Share proof' : ''} onRight={proof ? (()=>shareProof(proof)) : undefined}>Track record{sc.season?` · ${sc.season}`:''}</SectionLabel>
      <ProofHero sc={sc} proof={proof} />

      <div style={{ display:'flex', gap:6, margin:'2px 0 12px' }}>
        {[['record','Record'],['top20','Top 20'],['breakdown','Breakdown'],['model','Model']].map(([id,label])=>{
          const active = id === sub
          return (
            <button key={id} onClick={()=>setSub(id)} style={{ flex:1, padding:'8px 0', borderRadius:8, cursor:'pointer',
              fontWeight:800, fontSize:12, border:`1px solid ${active?T.green:T.border}`,
              background:active?T.greenDim:T.card, color:active?T.green:T.muted }}>{label}</button>
          )
        })}
      </div>

      {sub === 'top20' && <Top20Weekly season={season} />}

      {sub === 'record' && (<>
      {(() => { const rz = sc.realized; if(!rz || (!rz.plays && !rz.clv_n)) return null
        const clvUp = rz.mean_clv != null && rz.mean_clv > 0
        return (
          <>
            <SectionLabel>At the real book price</SectionLabel>
            <div style={{ fontSize:10.5, color:T.muted, background:T.card2, border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 11px", marginBottom:8, lineHeight:1.5 }}>
              If you'd bet the <b style={{color:T.muted2}}>actual posted number</b> (not our own line), here's how it went — the honest, out-of-sample result.
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:6 }}>
              <Stat label="Hit rate" value={rz.plays?`${rz.hit_rate}%`:'—'} color={rz.hit_rate!=null?hitColor(rz.hit_rate):T.muted}/>
              <Stat label="Record" value={rz.plays?`${rz.wins}-${rz.losses}`:'—'}/>
              <Stat label="Plays" value={rz.plays||0}/>
            </div>
            {rz.clv_n ? (
              <>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginBottom:6 }}>
                  <Stat label="Market moved to us" value={rz.clv_positive_rate!=null?`${rz.clv_positive_rate}%`:'—'} color={rz.clv_positive_rate>=50?T.green:T.amber}/>
                  <Stat label="Line value (CLV)" value={`${clvUp?'+':''}${rz.mean_clv} pts`} color={clvUp?T.green:(rz.mean_clv<0?T.red:T.muted)}/>
                </div>
                <div style={{ fontSize:10, color:T.faint, marginBottom:14, lineHeight:1.5 }}>
                  "Market moved to us" = how often the line moved toward our side after we posted — a sign the pick was ahead of the market.
                </div>
              </>
            ) : <div style={{ marginBottom:14 }}/>}
          </>
        ) })()}

      <WeeklyTrend data={wr} />
      </>)}

      {sub === 'breakdown' && (<>
      {tr?.tiers?.length ? (
        <>
          <SectionLabel>Accuracy by conviction</SectionLabel>
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden", marginBottom:14 }}>
            {tr.tiers.map((t,i)=>(
              <div key={t.tier} style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"10px 12px", borderTop:i?`1px solid ${T.border}`:"none" }}>
                <span style={{ fontSize:13, fontWeight:700, color:T.text }}>{TIER_LABEL[t.tier]||t.tier}</span>
                <span style={{ fontSize:11, color:T.muted }}>{t.plays} plays</span>
                <span style={{ fontFamily:T.mono, fontWeight:800, color:hitColor(t.hit_rate), minWidth:52, textAlign:"right" }}>{t.hit_rate}%</span>
              </div>
            ))}
          </div>
        </>
      ) : null}


      <SectionLabel>By market</SectionLabel>
      {props.length ? (
        <>
        <div style={{ fontSize:10.5, color:T.muted, margin:'2px 0 8px', lineHeight:1.5 }}>
          Which markets actually <b style={{color:T.text}}>beat the book</b> — and where the market moved our way after we posted (CLV). This is what our edge looks like per market, honest and market-graded.
        </div>
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", fontSize:10, color:T.muted, padding:"7px 14px",
            letterSpacing:"0.05em", borderBottom:`1px solid ${T.border}` }}>
            <span style={{ flex:1 }}>MARKET</span>
            <span style={{ width:96, textAlign:"right" }}>BOOK LINE</span>
            <span style={{ width:58, textAlign:"right" }}>CLV</span>
          </div>
          {props.map(([k,v],i)=>{
            const rz = v.realized || {}
            const hasBook = rz.plays > 0
            const clvUp = rz.mean_clv != null && rz.mean_clv > 0
            return (
            <div key={k} style={{ display:"flex", alignItems:"center",
              padding:"11px 14px", borderTop: i?`1px solid ${T.border}`:"none" }}>
              <span style={{ flex:1, minWidth:0 }}>
                <span style={{ fontWeight:700 }}>{PROP_LABEL[k]||k}</span>
                <span style={{ fontFamily:T.mono, fontSize:10.5, color:T.faint, marginLeft:8 }}>model {v.hit_rate}%</span>
              </span>
              <span style={{ width:96, textAlign:"right", fontFamily:T.mono, fontSize:12.5 }}>
                {hasBook ? <>
                  <span style={{ color:hitColor(rz.hit_rate) }}>{rz.hit_rate}%</span>
                  <span style={{ color:T.muted, marginLeft:6 }}>{rz.wins}-{rz.losses}</span>
                </> : <span style={{ color:T.faint }}>—</span>}
              </span>
              <span style={{ width:58, textAlign:"right", fontFamily:T.mono, fontSize:12.5,
                color: rz.clv_n ? (clvUp?T.green:(rz.mean_clv<0?T.red:T.muted)) : T.faint }}>
                {rz.clv_n ? `${clvUp?'+':''}${rz.mean_clv}` : '—'}
              </span>
            </div>
          )})}
        </div>
        <div style={{ fontSize:10, color:T.faint, marginTop:6, lineHeight:1.5 }}>
          <b style={{color:T.muted}}>Book line</b> = record betting the real posted number (a dash = no priced plays graded yet in that market). <b style={{color:T.muted}}>CLV</b> = average points the market moved toward us after we posted; positive is sharp. <b style={{color:T.muted}}>model</b> = hit rate at our own reference line.
        </div>
        </>
      ) : <Empty message="Record populates after the first settled NFL week"/>}

      {/* Pay Dirt — the red-zone TD board's own graded record (frozen weekly, settled vs who scored). */}
      {pd && pd.overall && pd.overall.plays > 0 && (
        <>
          <SectionLabel>🏈 Pay Dirt · red-zone TDs <span style={{ fontSize:10, fontWeight:800, color:T.amber, border:`1px solid ${T.amber}66`, borderRadius:6, padding:"1px 6px", marginLeft:6, letterSpacing:0.5, textTransform:'uppercase', verticalAlign:'middle' }}>In testing</span></SectionLabel>
          <div style={{ fontSize:10.5, color:T.muted, margin:'2px 0 8px', lineHeight:1.5 }}>
            Still being calibrated — tracked for transparency, <b style={{ color:T.muted2 }}>not counted in the main record yet.</b>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:6 }}>
            <Stat label="Record" value={`${pd.overall.wins}-${pd.overall.losses}`} color={T.green}/>
            <Stat label="To-score hit" value={pd.overall.hit_rate!=null?`${pd.overall.hit_rate}%`:'—'} color={T.text}/>
            <Stat label="ROI at book price" value={pd.overall.roi!=null?`${pd.overall.roi>0?'+':''}${pd.overall.roi}%`:'—'} color={pd.overall.roi>0?T.green:(pd.overall.roi<0?T.red:T.muted)}/>
          </div>
          <div style={{ fontSize:10, color:T.faint, marginBottom:12, lineHeight:1.5 }}>
            TD props mostly pay plus money, so hitting about <b style={{color:T.muted}}>45%</b> breaks even — the hit rate alone undersells it.{pd.overall.priced?` ROI over ${pd.overall.priced} priced plays.`:''}
          </div>
          {pd.value && pd.value.plays>0 && (
            <div style={{ fontSize:11, color:T.muted2, background:T.greenDim, border:`1px solid ${T.greenMid||T.green}55`, borderRadius:8, padding:"8px 11px", marginBottom:14, lineHeight:1.5 }}>
              <b style={{ color:T.green }}>✓ VALUE plays: {pd.value.hit_rate!=null?`${pd.value.hit_rate}%`:'—'} hit</b> <span style={{ color:T.muted }}>({pd.value.plays} +EV picks)</span>
            </div>
          )}
        </>
      )}

      {gr && gr.games_graded > 0 && (() => {
        const cell = (rec, label) => {
          const hr = rec && rec.hit_rate
          return (
            <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, padding:"10px 8px", textAlign:"center" }}>
              <div style={{ fontFamily:T.mono, fontWeight:800, fontSize:20, color: hr!=null?hitColor(hr):T.muted }}>{hr!=null?`${hr}%`:'—'}</div>
              <div style={{ fontSize:10.5, color:T.muted, marginTop:2 }}>{label}</div>
              <div style={{ fontSize:10, color:T.faint, marginTop:1 }}>{rec&&rec.plays?`${rec.wins}-${rec.losses}${rec.pushes?`-${rec.pushes}`:''}`:'—'}</div>
            </div>
          )
        }
        return (
          <>
            <button onClick={()=>setGmOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
              background:T.card, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 12px', margin:'14px 0 8px', cursor:'pointer', color:T.text }}>
              <span style={{ fontSize:12.5, fontWeight:800 }}>Game model · {gr.games_graded} games <span style={{ fontSize:10, fontWeight:800, color:T.amber, border:`1px solid ${T.amber}66`, borderRadius:6, padding:"1px 6px", marginLeft:6, letterSpacing:0.5, textTransform:'uppercase' }}>In testing</span></span>
              <span style={{ fontSize:11, color:T.muted }}>{gmOpen ? 'Hide ▲' : 'Show ▼'}</span>
            </button>
            {gmOpen && <>
            <div style={{ fontSize:10.5, color:T.muted, margin:'2px 0 8px', lineHeight:1.5 }}>
              Our game reads (spread, total, moneyline) vs the closing line, live only. Our edge is player props; this side of the model is still being tuned and isn't part of the main record. From Week 9 a game counts only when the model really disagrees with the market (2+ pts off the spread or total, or a moneyline underdog pick).{(gr.ou?.no_edge||0)+(gr.ats?.no_edge||0) ? ` ${(gr.ou?.no_edge||0)+(gr.ats?.no_edge||0)} no-edge reads not counted.` : ''}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:10 }}>
              {cell(gr.ats, 'ATS · spread')}{cell(gr.ou, 'Total · O/U')}{cell(gr.ml, gr.ml_favorite_won_pct!=null?`Moneyline · favs won ${gr.ml_favorite_won_pct}%`:'Moneyline')}
            </div>
            {gr.win_prob?.buckets?.length ? (
              <>
                <div style={{ fontSize:10.5, color:T.muted, margin:'0 0 6px' }}>
                  Win-probability calibration{gr.win_prob.brier!=null?<> · <b style={{color:T.muted2}}>Brier {gr.win_prob.brier}</b></>:''} — when we make a side a favorite, does it win that often? (Lower is better: a coin flip scores 0.250; closing lines run about 0.21.)
                </div>
                <ReliabilityDiagram buckets={widenBuckets(gr.win_prob.buckets)} />
              </>
            ) : null}
            </>}
          </>
        )
      })()}
      </>)}

      {sub === 'model' && (<>
      <SectionLabel>Fantasy accuracy</SectionLabel>
      {fa?.overall?.n ? (
        <div style={{ marginBottom:14 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:8 }}>
            <Stat label="Avg miss (MAE)" value={`${fa.overall.mae} pts`} color={maeColor(fa.overall.mae)}/>
            <Stat label="Rank accuracy" value={fa.overall.spearman!=null?fa.overall.spearman:'—'} color={corrColor(fa.overall.spearman)}/>
            <Stat label="Bias" value={fa.overall.bias!=null?`${fa.overall.bias>0?'+':''}${fa.overall.bias}`:'—'}/>
          </div>
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden", marginBottom:8 }}>
            <div style={{ display:"flex", fontSize:10, color:T.muted, padding:"7px 12px", letterSpacing:"0.04em", borderBottom:`1px solid ${T.border}` }}>
              <span style={{ flex:1 }}>POSITION</span><span style={{ width:70, textAlign:"right" }}>MAE</span>
              <span style={{ width:70, textAlign:"right" }}>RANK r</span><span style={{ width:54, textAlign:"right" }}>N</span>
            </div>
            {FPOS.map(p => { const d = fa.by_pos?.[p]; if(!d?.n) return null; return (
              <div key={p} style={{ display:"flex", alignItems:"center", padding:"9px 12px", borderTop:`1px solid ${T.border}`, fontSize:12.5 }}>
                <span style={{ flex:1, fontWeight:700 }}>{p}</span>
                <span style={{ width:70, textAlign:"right", fontFamily:T.mono, color:maeColor(d.mae) }}>{d.mae}</span>
                <span style={{ width:70, textAlign:"right", fontFamily:T.mono, color:corrColor(d.spearman) }}>{d.spearman!=null?d.spearman:'—'}</span>
                <span style={{ width:54, textAlign:"right", fontFamily:T.mono, color:T.muted }}>{d.n}</span>
              </div>
            )})}
          </div>
          {fa.tiers?.some(t=>t.n) ? (
            <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, overflow:"hidden", marginBottom:8 }}>
              <div style={{ fontSize:10.5, color:T.muted, padding:"8px 12px 4px" }}>Do our projected tiers actually separate? (avg REAL points scored)</div>
              {fa.tiers.filter(t=>t.n).map((t,i)=>(
                <div key={t.tier} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 12px", borderTop:`1px solid ${T.border}` }}>
                  <span style={{ fontSize:12, color:T.muted }}>{t.tier}</span>
                  <span style={{ fontFamily:T.mono, fontWeight:800, color:T.text }}>{t.avg_actual} pts <span style={{ color:T.faint, fontWeight:400 }}>· {t.n}</span></span>
                </div>
              ))}
            </div>
          ) : null}
          <div style={{ fontSize:10, color:T.muted, lineHeight:1.5 }}>
            Projected vs actual fantasy points, graded walk-forward over {(fa.seasons||[]).join(', ')} on {fa.overall.n.toLocaleString()} player-weeks · PPR.
            {fa.starters?.n ? <> Startable players (≥10 proj): MAE <b style={{color:maeColor(fa.starters.mae)}}>{fa.starters.mae}</b> on {fa.starters.n.toLocaleString()}.</> : null}
            {' '}Rank accuracy (Spearman, 1.0 = perfect order) is what matters most for start/sit.
          </div>
        </div>
      ) : <div style={{ fontSize:11, color:T.muted, marginBottom:14 }}>Fantasy accuracy grades our projected points against what players actually scored — it fills in as the season's results settle.</div>}

      <SectionLabel right={cal?.brier!=null?`Brier ${cal.brier}`:''}>Calibration</SectionLabel>
      <div style={{ fontSize:10.5, color:T.muted, margin:'2px 0 8px', lineHeight:1.5 }}>
        When we say a play is 65% to hit, does it actually hit ~65% of the time? This checks — the closer <b style={{color:T.text}}>observed</b> tracks <b style={{color:T.text}}>predicted</b> across every band, the more honest our percentages.
      </div>
      {cal?.buckets?.length ? (
        <>
        <ReliabilityDiagram buckets={cal.buckets} />
        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:10, padding:'10px 14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:T.muted, marginBottom:6, letterSpacing:'0.04em' }}>
            <span>PREDICTED</span><span>OBSERVED</span><span>N</span>
          </div>
          {cal.buckets.map((b,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'5px 0',
              borderTop:i?`1px solid ${T.border}`:'none', fontSize:12, fontFamily:T.mono }}>
              <span style={{ width:56, color:T.muted }}>{b.label}</span>
              <span style={{ flex:1 }}>
                <span style={{ display:'inline-block', width:`${b.pred_pct}%`, height:6, background:T.faint, borderRadius:3, verticalAlign:'middle' }}/>
              </span>
              <span style={{ width:44, textAlign:'right', color:hitColor(b.obs_pct) }}>{b.obs_pct}%</span>
              <span style={{ width:34, textAlign:'right', color:T.muted }}>{b.n}</span>
            </div>
          ))}
          <div style={{ fontSize:10, color:T.muted, marginTop:8, lineHeight:1.5 }}>Observed hit rate vs the model's predicted probability — the two matching = well-calibrated.{cal.brier!=null?<> <b style={{color:T.muted2}}>Brier {cal.brier}</b> grades overall accuracy — lower is better (0 = perfect).</>:''}</div>
        </div>
        </>
      ) : <div style={{ fontSize:11, color:T.muted }}>Calibration builds from settled results — it fills in as games grade this season.</div>}
      </>)}
    </div>
  )
}
